import styled, { css } from 'styled-components';

import { rem, mediaUp } from 'approot/shared/style';
import { baseBoxShadowFocus } from 'approot/shared/styles/borders.style';
import {
  grey1,
  grey3,
  grey5,
  grey9,
  purple6,
  purple7,
} from 'approot/shared/styles/colours.style';
import { fontSize16 } from 'approot/shared/styles/typography.style';
import {
  cardHoverBoxShadow,
  cardHoverTransition,
} from 'approot/shared/styles/cards.style';
import { spacing24 } from 'approot/shared/styles/spacing.style';
import { flexCentreAlignCss } from 'approot/shared/styles/flex.style';

type WorksheetCardProps = {
  $isDisabled?: boolean;
  $isSelected?: boolean;
  $ratio?: number;
};

export const WorksheetCard = styled.div<WorksheetCardProps>`
  text-align: left;
  border-radius: ${rem(8)};
  position: relative;
  flex-grow: 1;
  border: 1px solid ${grey5};
  overflow: hidden;

  ${({ $isDisabled }) => ($isDisabled ? '' : cardHoverTransition)}
  ${({ $isSelected }) =>
    $isSelected &&
    `
    border: 1px solid ${purple6};
  `}

  &:focus {
    border: 1px solid ${purple7};
  }

  /* MS Egge fix for padding-based aspect ratio on a flex child!
  Instead of applying the padding to the element, we need to apply it to a pseudo element.
  Explanation by Crisy Coyier found buried in the comments here: https://css-tricks.com/aspect-ratio-boxes/
  Summary:
  "...if an aspect ratio container is a child of a flex container,
  this breaks horribly in MS Edge 15 and 16 – and maybe some recent
  but older versions of Firefox. Padding-bottom will be 0 no  matter what rule you apply." */
  &::before {
    content: '';
    width: 1px;
    margin-left: -1px;
    float: left;
    height: 0;
    padding-top: ${({ $ratio }) => ($ratio ? $ratio : '100')}%;
  }
  &::after {
    content: '';
    display: table;
    clear: both;
  }

  a:hover + & {
    box-shadow: ${cardHoverBoxShadow};
  }

  a:focus + & {
    border: 1px solid ${grey1};
    box-shadow: ${baseBoxShadowFocus};
  }

  a:hover:focus + & {
    box-shadow: ${baseBoxShadowFocus}, ${cardHoverBoxShadow};
  }
`;

export const WorksheetCardTitle = styled.div`
  color: ${grey9};
  font-size: ${rem(fontSize16)};
  margin-top: ${rem(12)};
`;

type OverlayProps = {
  $ratio?: number;
};

const WorksheetCardOverlayCss = css<OverlayProps>`
  position: absolute;
  top: 0;
  width: 100%;
  padding-top: ${({ $ratio }) => ($ratio ? $ratio : '100')}%;
`;

type WorksheetCardImageProps = OverlayProps & {
  $imageUrl?: string;
  $imageHasLoaded: boolean;
  $isLandscape: boolean;
};

export const WorksheetCardImage = styled.div.attrs<WorksheetCardImageProps>(
  ({ $imageUrl }) => ({
    style: {
      backgroundImage: $imageUrl ? `url(${$imageUrl})` : '',
    },
  })
)<WorksheetCardImageProps>`
  ${WorksheetCardOverlayCss}
  transition: opacity 1s;
  opacity: ${({ $imageHasLoaded }) => ($imageHasLoaded ? '1' : '0')};
  background-size: ${({ $isLandscape }) =>
    $isLandscape ? '100% auto' : 'auto 100%'};
  background-repeat: no-repeat;
  background-position: center;
  border-radius: ${rem(8)};
`;

type CardWrapperProps = {
  $buttonSpace?: boolean;
} & JSX.IntrinsicElements['div'];

export const WorksheetCardWrapper = styled.div<CardWrapperProps>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(spacing24)};

  ${mediaUp.sm<CardWrapperProps>`
    ${({ $buttonSpace }) =>
      $buttonSpace &&
      `
      padding-bottom: ${rem(70)};`}
  `}
`;

type WorksheetCardImageWrapperProps = {
  $isLandscape: boolean;
};
export const WorksheetCardImageWrapper = styled.div<WorksheetCardImageWrapperProps>`
  ${flexCentreAlignCss}
  background-color: ${grey3};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  img {
    background-color: ${grey1};

    ${({ $isLandscape }) =>
      $isLandscape
        ? `
        width: 100%;
      `
        : `
        height: 100%;
      `};
  }
`;
